import { Avatar, Box, Divider, Drawer, Grid, Typography } from '@material-ui/core';
import classNames from 'classnames';
import React, { useMemo, useCallback, useState } from 'react';

import { UserRoleBadge } from '~/features/dashboard/components/user-role';
import { User } from '~/store/user/models';
import css from './styles.module.scss';

import { useRouteMatch } from 'react-router-dom';
import { goToEditContactThunk, goToEditCreatorThunk } from '~/features/dashboard/store/actions.thunks';
import EditIcon from '@material-ui/icons/Edit';
import { useThunkDispatch } from '~/utils/hooks/useThunkDispatch';

import { IconButton, List, ListItem, ListItemText } from '@material-ui/core';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import BusinessIcon from '@material-ui/icons/Business';
import PhoneIphoneIcon from '@material-ui/icons/PhoneIphone';
import ImageIcon from '@material-ui/icons/Image';
import { VerticalPopver } from '~/features/dashboard/components/vertical-popover';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import { SocietyListDrawer } from '~/features/dashboard/components/editable-list-drawer';
import { EditContactListDrawer } from '~/features/dashboard/components/new-edit-contact';
import { resetNewContactForm } from '~/features/dashboard/store/actions';

import FacebookIcon from '@material-ui/icons/Facebook';
import AppleIcon from '@material-ui/icons/Apple';
import { AKAsListDrawer } from '~/features/dashboard/components/user-AKAs';
import { useSelector } from 'react-redux';
import { getAKAsList } from '~/features/dashboard/store/selectors';

enum DRAWER_TYPES {
    CREATOR = 'CREATOR',
    LEGALENTITY = 'LEGALENTITY',
    SOCIETY = 'SOCIETY',
    AKAS = 'AKAS',
}
const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        popover: {
            pointerEvents: 'none',
        },
        popoverContent: {
            pointerEvents: 'auto',
        },
        root: {
            width: '100%',
            maxWidth: 360,
            backgroundColor: theme.palette.background.paper,
        },
        large: {
            width: theme.spacing(20),
            height: theme.spacing(20),
            margin: 'auto',
        },
    })
);

interface UserProfileMainProps {
    className?: string;
    user: User;
}

export const UserProfileMain: React.FC<UserProfileMainProps> = ({ className, user }) => {
    const dispatch = useThunkDispatch();
    const match = useRouteMatch<{ creatorId: string }>();
    const creatorId = useMemo(() => Number(match.params.creatorId), [match.params.creatorId]);
    const AKAsList = useSelector(getAKAsList);

    const editContactForm = useCallback(() => {
        dispatch(goToEditCreatorThunk({ kontactId: user.kontactId, projectId: creatorId, creatorCode: user.creatorCode }));
        openCreatorDrawer();
        // eslint-disable-next-line
    }, [user.kontactId, dispatch, creatorId, user.creatorCode]);

    const classes = useStyles();

    const iconPhone = (num: any) => {
        return num === 1 ? <PhoneIphoneIcon className={css.iconPhone} /> : <BusinessIcon className={css.iconPhone} />;
    };

    const [drawerData, setDrawerData] = useState<{ isOpen: boolean; type?: DRAWER_TYPES; meta?: any }>({ isOpen: false });
    const closeDrawer = () => {
        setDrawerData({ isOpen: false, type: undefined, meta: undefined });
    };
    const openDrawer = (type: DRAWER_TYPES, meta?: any) => {
        setDrawerData({ isOpen: true, type, meta });
    };
    const openCreatorDrawer = () => {
        openDrawer(DRAWER_TYPES.CREATOR);
    };
    const openLegalEntityDrawer = () => {
        openDrawer(DRAWER_TYPES.LEGALENTITY);
    };
    const openSocietyDrawer = () => {
        openDrawer(DRAWER_TYPES.SOCIETY);
    };
    const openAKAsDrawer = () => {
        openDrawer(DRAWER_TYPES.AKAS);
    };

    return (
        <div className={classNames(css.module, className)}>
            <Grid container className={css.content}>
                <Grid item xs={8}>
                    <Grid container>
                        <Grid item xs={3}>
                            <div className={css.boxAvatar}>
                                <Avatar
                                    alt={user.name}
                                    src={user.image}
                                    className={classNames(classes.large, css.customAvatar)}
                                />
                                <div className={css.usernameContainer}>
                                    <Typography className={classNames(css.bold, css.username)}>{user.name}</Typography>
                                </div>
                                <UserRoleBadge role={user.role} className={css.role} />
                                <div style={{ display: 'flex', justifyContent: 'center', marginTop: '10px' }}>
                                    <FacebookIcon />
                                    <AppleIcon />
                                </div>
                            </div>
                        </Grid>
                        <Grid item xs={9}>
                            <div className={css.box}>
                                <Grid container spacing={3}>
                                    <Grid item xs={6} style={{ position: 'relative' }}>
                                        <Typography className={classNames(css.bold, css.infoTitle)}>AKAs (Also known as)</Typography>
                                        <div className={css.scrollSociety}>
                                            <List className={css.akaList}>
                                                {AKAsList &&
                                                    AKAsList.map((item: any) => (
                                                        <ListItem key={item.aka}>
                                                            <Avatar style={{ borderRadius: '5px', marginRight: '10px' }}>
                                                                <ImageIcon />
                                                            </Avatar>
                                                            <ListItemText className={css.infoContent} primary={item.aka} />
                                                        </ListItem>
                                                    ))}
                                            </List>
                                        </div>
                                        <VerticalPopver childClassName={css.verticalPopver1} actionsList={`edit`} onClickEdit={openAKAsDrawer} />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <div className={classNames(css.boxSociety)}>
                                            <Grid container>
                                                <Grid item xs={5}>
                                                    <Typography className={classNames(css.bold, css.infoTitle)}>Society</Typography>
                                                </Grid>
                                                <Grid item xs={7}>
                                                    <Typography className={classNames(css.bold, css.infoTitle)}>IPI/CAE number</Typography>
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <Grid container className={css.scrollSociety}>
                                                        <List className={css.akaList}>
                                                            {user.ipiList.map(({ affiliation, iPNumber }) => (
                                                                <ListItem key={`affiliation-iPNumber-${affiliation}-${iPNumber}`}>
                                                                    <Typography
                                                                        key={`affiliation-typography-${affiliation}`}
                                                                        style={{ marginRight: '10px', width: '39.666667%' }}
                                                                        className={classNames(css.infoContent)}
                                                                    >
                                                                        {affiliation}
                                                                    </Typography>
                                                                    <Typography
                                                                        style={{ width: '60.333333%' }}
                                                                        key={`iPNumber-typography-${iPNumber}`}
                                                                        className={classNames(css.infoContent)}
                                                                    >
                                                                        {iPNumber}
                                                                    </Typography>
                                                                </ListItem>
                                                            ))}
                                                        </List>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                            <VerticalPopver childClassName={css.verticalPopver} actionsList={`edit, add`} onClickEdit={openSocietyDrawer} />
                                        </div>
                                    </Grid>
                                </Grid>
                            </div>
                        </Grid>
                        <Grid item xs={12}>
                            <div style={{ minHeight: '300px' }} className={css.box}>
                                <Grid container className={css.gridContainer} spacing={3}>
                                    <Grid item xs={6}>
                                        <Typography style={{ marginBottom: '15px' }} className={classNames(css.bold, css.infoTitle)}>
                                            Address
                                        </Typography>
                                        {(user.addressLine1 || user.addressLine2 || user.addressLine3 || user.addressLine4 || user.zipCode) && (
                                            <div className={css.address}>
                                                <Typography>{user.addressLine1}</Typography>
                                                <Typography style={user.addressLine1 ? { marginTop: '10px' } : {}}>
                                                    {`
                                                        ${user.addressLine2 || ''}
                                                        ${(user.addressLine3 === user.addressLine4 ? '' : user.addressLine3) || ''}
                                                    `}
                                                </Typography>
                                                <Typography style={{ marginTop: '10px' }}>{user.zipCode}</Typography>
                                                <Typography style={{ marginTop: '10px' }}>{user.addressLine4}</Typography>
                                            </div>
                                        )}
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography className={classNames(css.bold, css.infoTitle)}>Email</Typography>
                                        {user.email && <Typography className={classNames(css.infoContent)}>{user.email}</Typography>}
                                    </Grid>
                                </Grid>
                                <Grid container spacing={3} className={css.gridContainer}>
                                    {user.phone && (
                                        <Grid item xs>
                                            <Typography className={classNames(css.bold, css.infoTitle)}>{iconPhone(user.phoneType)} Phone</Typography>
                                            {user.phone && <Typography className={classNames(css.infoContent)}>{user.phone}</Typography>}
                                        </Grid>
                                    )}
                                    {user.phone2 && (
                                        <Grid item xs>
                                            <Typography className={classNames(css.bold, css.infoTitle)}>{iconPhone(user.phoneType2)} Phone</Typography>
                                            <Typography className={classNames(css.infoContent)}>{user.phone2}</Typography>
                                        </Grid>
                                    )}
                                    {user.phone3 && (
                                        <Grid item xs>
                                            <Typography className={classNames(css.bold, css.infoTitle)}>{iconPhone(user.phoneType3)} Phone</Typography>
                                            <Typography className={classNames(css.infoContent)}>{user.phone3}</Typography>
                                        </Grid>
                                    )}
                                    {user.phone4 && (
                                        <Grid item xs>
                                            <Typography className={classNames(css.bold, css.infoTitle)}>{iconPhone(user.phoneType4)} Phone</Typography>
                                            <Typography className={classNames(css.infoContent)}>{user.phone4}</Typography>
                                        </Grid>
                                    )}
                                </Grid>
                                <VerticalPopver childClassName={css.verticalPopver} actionsList={`edit`} onClickEdit={editContactForm} />
                            </div>
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item xs={4}>
                    <Grid className={css.box} item xs>
                        <Typography style={{ marginTop: '0' }} className={classNames(css.bold, css.username)}>
                            Legal Entity
                        </Typography>
                        {user.contacts.filter(contact => contact.contactTypeDesc === 'My Legal Entity').length > 0 && (
                            <List className={css.leList}>
                                {user.contacts
                                    .filter(contact => contact.contactTypeDesc === 'My Legal Entity')
                                    .map(contact => (
                                        <ListItem key={`legal-entity-${contact.contactId}`} style={{ display: 'block' }}>
                                            <Typography className={classNames(css.bold, css.infoTitleLE)}>{contact.companyName.toUpperCase()}</Typography>
                                            <div style={{ position: 'relative' }} className={css.flex}>
                                                <Avatar className={css.legalEntityAvatar} src={contact.image}>
                                                    <ImageIcon />
                                                </Avatar>
                                                <div className={css.address}>
                                                    <Typography>{contact.addressLine1}</Typography>
                                                    <Typography style={contact.addressLine1 ? { marginTop: '10px' } : {}}>
                                                        {`
                                                            ${contact.addressLine2 || ''}
                                                            ${(contact.addressLine3 === contact.addressLine4 ? '' : contact.addressLine3) || ''}
                                                        `}
                                                    </Typography>
                                                    <Typography style={{ marginTop: '10px' }}>{contact.zipCode}</Typography>
                                                    <Typography style={{ marginTop: '10px' }}>{contact.addressLine4}</Typography>
                                                </div>
                                                <EditIcon
                                                    className={css.editIconLE}
                                                    onClick={() => {
                                                        dispatch(goToEditContactThunk({ contactId: contact.contactId, projectId: creatorId }));
                                                        openLegalEntityDrawer();
                                                    }}
                                                />
                                            </div>
                                        </ListItem>
                                    ))}
                            </List>
                        )}
                        <VerticalPopver
                            childClassName={css.verticalPopver}
                            actionsList={`add`}
                            onClickAdd={() => {
                                dispatch(resetNewContactForm());
                                openLegalEntityDrawer();
                            }}
                        />
                    </Grid>
                </Grid>
                <Grid item xs={12}>
                    <div className={css.box}>
                        <Grid item xs={12}>
                            <Typography className={classNames(css.bold, css.infoTitle)}>Description</Typography>
                            {user.description && <Typography className={classNames(css.infoContent)}>{user.description}</Typography>}
                        </Grid>
                    </div>
                </Grid>
            </Grid>
            <Drawer variant="persistent" anchor="right" open={drawerData.isOpen} id="deal-edit-step-6-drawer" className={css.rightDrawer}>
                <Box className={css.contentWrapper}>
                    <Box className={css.closeDrawer}>
                        <IconButton onClick={closeDrawer}>
                            <ArrowBackIcon />
                        </IconButton>
                        <Typography style={{ fontSize: '1rem' }}>Close</Typography>
                    </Box>
                    <Divider />
                    {drawerData.type === DRAWER_TYPES.CREATOR && <EditContactListDrawer role={0} title={'Creator'} closeDrawer={closeDrawer} />}
                    {drawerData.type === DRAWER_TYPES.LEGALENTITY && <EditContactListDrawer role={8} title={'Legal Entity'} closeDrawer={closeDrawer} />}
                    {drawerData.type === DRAWER_TYPES.SOCIETY && <SocietyListDrawer title={'SOCIETY'} closeDrawer={closeDrawer} />}
                    {drawerData.type === DRAWER_TYPES.AKAS && <AKAsListDrawer title={'AKAs (Also known as)'} closeDrawer={closeDrawer} />}
                </Box>
            </Drawer>
        </div>
    );
};
